import { useRef, useCallback, useEffect } from "react";
import * as PIXI from "pixi.js";
import { triggerScalePress } from "./useButtonHover";

// ── Centralized disabled button styling ──────────────────────────────────────
// Change these once → every secondary button updates automatically.
export const BUTTON_DISABLED_ALPHA = 0.35;
export const BUTTON_DISABLED_CURSOR = "default";
export const BUTTON_ENABLED_CURSOR = "pointer";

/**
 * Reusable button interaction hook for secondary theme PixiJS buttons.
 *
 * Provides:
 * - Brightness hover effect via ColorMatrixFilter
 * - Press animation via triggerScalePress (squish → bounce back)
 * - Cleanup on unmount via cleanupRef
 * - Centralized disabled styling: `alpha`, `cursor` (derived from `disabled` flag)
 *
 * Edge artifact fix:
 * - brightness(1.35) instead of 2.0 — gentler brightening avoids pushing
 *   semi-transparent edge pixels beyond their alpha boundary
 * - padding = 8 — ensures the filter framebuffer fully covers the sprite
 *   with enough margin to prevent edge clipping
 * - resolution = devicePixelRatio — matches the renderer's pixel density
 *
 * @param {Object} [options]
 * @param {number} [options.brightness=1.35] - Hover brightness multiplier (1.0 = normal)
 * @param {boolean} [options.disabled=false] - When true, hover effect is suppressed and filters are cleared
 * @returns {{ buttonRef, handlePointerOver, handlePointerOut, triggerPress, cleanupFilters, alpha, cursor }}
 */
export const useButtonBrightness = ({ brightness = 1.35, disabled = false } = {}) => {
  const buttonRef = useRef(null);
  const isPressAnimatingRef = useRef(false);

  // Create the brightness filter once
  const brightnessFilter = useRef(null);
  if (!brightnessFilter.current) {
    brightnessFilter.current = new PIXI.ColorMatrixFilter();
    brightnessFilter.current.brightness(brightness, false);
    brightnessFilter.current.resolution = window.devicePixelRatio || 1;
    // Large padding prevents edge clipping artifacts on curved/transparent edges
    brightnessFilter.current.padding = 8;
  }

  // Auto-clear filters when button becomes disabled (handles mid-hover disable)
  useEffect(() => {
    if (disabled && buttonRef.current) {
      buttonRef.current.filters = [];
    }
  }, [disabled]);

  const handlePointerOver = useCallback(() => {
    if (disabled) return;
    const sprite = buttonRef.current;
    if (sprite && (!sprite.filters || sprite.filters.length === 0)) {
      sprite.filters = [brightnessFilter.current];
    }
  }, [disabled]);

  const handlePointerOut = useCallback(() => {
    const sprite = buttonRef.current;
    if (sprite) {
      sprite.filters = [];
    }
  }, []);

  /**
   * Trigger a squish-bounce press animation on the button sprite.
   */
  const triggerPress = useCallback(() => {
    const sprite = buttonRef.current;
    if (!sprite || isPressAnimatingRef.current) return;

    isPressAnimatingRef.current = true;
    triggerScalePress(sprite);

    // Reset flag after animation completes (65ms + 120ms = 185ms)
    setTimeout(() => {
      isPressAnimatingRef.current = false;
    }, 200);
  }, []);

  /**
   * Cleanup: remove filters from sprite. Call in useEffect return.
   */
  const cleanupFilters = useCallback(() => {
    const sprite = buttonRef.current;
    if (sprite) sprite.filters = [];
  }, []);

  // ── Derived disabled props (centralized) ─────────────────────────────────
  const alpha = disabled ? BUTTON_DISABLED_ALPHA : 1;
  const cursor = disabled ? BUTTON_DISABLED_CURSOR : BUTTON_ENABLED_CURSOR;

  return {
    buttonRef,
    handlePointerOver,
    handlePointerOut,
    triggerPress,
    cleanupFilters,
    alpha,
    cursor,
  };
};
